package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceOptionsResponse
import ai.jessie.shared.invoices.InvoiceWorkInput
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.LabelStyle
import ai.jessie.web.theme.LegendStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

// Step 5 and A1: the member chooses the next position type; nothing is sent until it is added.
// Shown in the positions card, like "Freie Position" in `06-Rechnung.png`.
@Composable
internal fun lineTypeChoice(state: InvoiceUiState, actions: InvoiceActions) {
    val options = state.options ?: return
    val draft = state.draft ?: return
    Div(attrs = RowStyle.toAttrs()) {
        lineTypes.forEach { type ->
            themedButton(
                "${lineTypeLabel(type)} hinzufügen",
                { actions.startLine(type) },
                variant = ButtonVariant.TINT,
                size = ButtonSize.SMALL,
                disabled = state.busy,
            )
        }
    }
    // B2: position prices are denominated in the organisation default currency.
    if (draft.currency.isNotBlank() && draft.currency != options.defaultCurrency)
        P(attrs = HintStyle.toAttrs()) {
            Text(
                "Fixpreis- und Aufwandspositionen stehen nur in der Standardwährung " +
                    "${options.defaultCurrency} zur Verfügung. Freie Positionen sind in " +
                    "${draft.currency} möglich."
            )
        }
}

// Steps 6-7: the information the selected position type needs; submitting adds the position.
@Composable
internal fun invoiceLineForm(state: InvoiceUiState, actions: InvoiceActions) {
    val line = state.line ?: return
    val options = state.options ?: return
    Form(
        attrs =
            CardStyle.toAttrs {
                addEventListener("submit") {
                    it.preventDefault()
                    actions.addLine()
                }
            }
    ) {
        Fieldset(attrs = FieldsetStyle.toAttrs { if (state.busy) attr("disabled", "") }) {
            Legend(attrs = LegendStyle.toAttrs()) { Text(lineTypeLabel(line.type)) }
            Div(attrs = CardBodyStyle.toAttrs()) {
                if (line.type == FREE) freeFields(line, state, actions, options)
                else positionFields(line, state, actions, options)
            }
            Div(
                attrs =
                    CardFootStyle.toAttrs { style { property("justify-content", "flex-start") } }
            ) {
                themedButton(
                    "Position übernehmen",
                    {},
                    variant = ButtonVariant.PRIMARY,
                    submit = true,
                )
                themedButton("Verwerfen", actions.discardLine, disabled = state.busy)
            }
        }
    }
}

@Composable
private fun freeFields(
    line: InvoiceLineDraft,
    state: InvoiceUiState,
    actions: InvoiceActions,
    options: InvoiceOptionsResponse,
) {
    invoiceInput(
        InvoiceField(
            "line.description",
            "Beschreibung (Pflichtfeld, höchstens 500 Zeichen)",
            line.description,
            state.problems["line.description"],
        ),
        InputType.Text,
    ) { value ->
        actions.changeLine { it.copy(description = value) }
    }
    invoiceInput(
        InvoiceField(
            "line.quantity",
            "Menge (Pflichtfeld, positiv, höchstens vier Dezimalstellen)",
            line.quantity,
            state.problems["line.quantity"],
        ),
        InputType.Text,
        { attr("inputmode", "decimal") },
        decimal = true,
    ) { value ->
        actions.changeLine { it.copy(quantity = value) }
    }
    invoiceInput(
        InvoiceField(
            "line.unitPrice",
            "Preis pro Einheit in ${state.draft?.currency.orEmpty()} (Pflichtfeld, positiv, " +
                "höchstens zwei Dezimalstellen)",
            line.unitPrice,
            state.problems["line.unitPrice"],
        ),
        InputType.Text,
        { attr("inputmode", "decimal") },
        decimal = true,
    ) { value ->
        actions.changeLine { it.copy(unitPrice = value) }
    }
    // B4: only the rates applicable on the invoice date are offered.
    invoiceSelect(
        InvoiceField(
            "line.vatRateId",
            "Mehrwertsteuersatz (Pflichtfeld, gültig am Rechnungsdatum)",
            line.vatRateId,
            state.problems["line.vatRateId"],
        ),
        options.vatRates.associate { it.id to vatLabel(it.name, it.rate) },
    ) { value ->
        actions.changeLine { it.copy(vatRateId = value) }
    }
}

// Step 6, BR-077: only billable projects of the selected customer and positions of the chosen type.
@Composable
private fun positionFields(
    line: InvoiceLineDraft,
    state: InvoiceUiState,
    actions: InvoiceActions,
    options: InvoiceOptionsResponse,
) {
    if (options.projects.isEmpty())
        P(attrs = HintStyle.toAttrs()) {
            Text(
                "Zurzeit sind keine verrechenbaren Projekte und Positionen wählbar. Wähle zuerst " +
                    "einen Kunden und die Standardwährung ${options.defaultCurrency}."
            )
        }
    invoiceSelect(
        InvoiceField(
            "line.projectId",
            "Projekt (Pflichtfeld)",
            line.projectId,
            state.problems["line.projectId"],
        ),
        options.projects.associate { it.id to it.name },
    ) { value ->
        actions.changeLine {
            if (it.projectId == value) it
            else it.copy(projectId = value, positionId = "", work = emptyList())
        }
    }
    val positions =
        options.projects
            .firstOrNull { it.id == line.projectId }
            ?.positions
            ?.filter { it.type == line.type }
            .orEmpty()
    invoiceSelect(
        InvoiceField(
            "line.positionId",
            "Position (Pflichtfeld, nach Wahl des Projekts)",
            line.positionId,
            state.problems["line.positionId"],
        ),
        positions.associate {
            it.id to
                "${it.name} · ${money(options.defaultCurrency, it.unitPrice)} · " +
                    vatLabel(it.vatName, it.vatRate)
        },
    ) { value ->
        actions.changeLine {
            if (it.positionId == value) it else it.copy(positionId = value, work = emptyList())
        }
    }
    invoiceInput(
        InvoiceField(
            "line.description",
            "Beschreibung (optional, sonst Projekt und Position)",
            line.description,
            state.problems["line.description"],
        ),
        InputType.Text,
    ) { value ->
        actions.changeLine { it.copy(description = value) }
    }
    if (line.type == TIME_MATERIAL) workChoices(line, state, actions, options)
}

// Steps 6-7 and A2: only eligible unbilled whole-minute work on the selected position.
@Composable
private fun workChoices(
    line: InvoiceLineDraft,
    state: InvoiceUiState,
    actions: InvoiceActions,
    options: InvoiceOptionsResponse,
) {
    val position = options.position(line.projectId, line.positionId) ?: return
    Fieldset(attrs = listOf(FieldsetStyle, StackStyle).toAttrs { id("invoice-line-work") }) {
        Legend(attrs = LabelStyle.toAttrs { style { property("margin-bottom", "8px") } }) {
            Text("Verrechenbare Arbeit (Pflichtfeld, mindestens ein Eintrag)")
        }
        if (position.work.isEmpty())
            P(attrs = StatusStyle.toAttrs { attr("role", "status") }) {
                Text(
                    "Für diese Position gibt es keine verrechenbare Arbeit. Es wird keine " +
                        "Aufwandsposition hinzugefügt. Wähle eine andere Position oder ein anderes " +
                        "Projekt."
                )
            }
        position.work.forEach { work ->
            invoiceCheckbox(
                "invoice-work-${work.id}",
                "${work.workDate} · ${work.durationMinutes} Min." +
                    (work.description?.let { " · $it" } ?: ""),
                line.work.any { it.id == work.id },
            ) { selected ->
                actions.changeLine {
                    val others = it.work.filter { entry -> entry.id != work.id }
                    it.copy(
                        work =
                            if (selected) others + InvoiceWorkInput(work.id, work.revision)
                            else others
                    )
                }
            }
        }
        state.problems["line.work"]?.let { P(attrs = FieldErrorStyle.toAttrs()) { Text(it) } }
    }
}
